// PDF Generator Service for Capitolato using pdfmake with templates
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import {
    NINFA_MODELS,
    FINISH_OPTIONS,
    GLASS_OPTIONS,
    FIXING_OPTIONS,
    DRAINAGE_HOLES_OPTIONS,
    PLATES_OPTIONS,
    MILLING_OPTIONS,
    MODEL_RENDER_FOLDER,
    MODEL_DECLARATION_FILES
} from '../utils/capitolatoData';

// Initialize pdfMake with fonts
if (pdfFonts && pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) {
    pdfMake.vfs = pdfFonts.pdfMake.vfs;
} else if (pdfFonts && pdfFonts.default && pdfFonts.default.pdfMake) {
    pdfMake.vfs = pdfFonts.default.pdfMake.vfs;
}

// Template paths - images for branding
const FIRST_PAGE_LOGO = '/images/capitolato/static/logo_prima_pagina.png';
const FOOTER_IMAGE = '/images/capitolato/static/piedipagina_altre_pagine.png';

// Helper function to convert image to base64
const getImageDataUrl = async (imageUrl) => {
    try {
        const response = await fetch(imageUrl);
        if (!response.ok) {
            console.warn(`Image not found: ${imageUrl}`);
            return null;
        }
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.startsWith('image/')) {
            console.warn(`Not an image (${contentType}): ${imageUrl}`);
            return null;
        }
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result;
                if (result && result.startsWith('data:image/')) {
                    resolve(result);
                } else {
                    console.warn(`Invalid dataURL for: ${imageUrl}`);
                    resolve(null);
                }
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.error('Error loading image:', imageUrl, error);
        return null;
    }
};

// Helper function to get image dimensions
const getImageDimensions = (dataUrl) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            resolve({ width: img.width, height: img.height });
        };
        img.onerror = reject;
        img.src = dataUrl;
    });
};

// Generate PDF document definition matching the exact template
const createDocDefinition = async (formData) => {
    // Get selected options
    const glassOption = GLASS_OPTIONS.find(g => g.value === formData.spessoreVetro);
    const fixingOption = FIXING_OPTIONS.find(f => f.value === formData.tipoFissaggio);
    const drainageOption = DRAINAGE_HOLES_OPTIONS.find(d => d.value === formData.foriScarico);
    const platesOption = PLATES_OPTIONS.find(p => p.value === formData.piattiScarico);
    const millingOption = MILLING_OPTIONS.find(m => m.value === formData.fresaturaScarico);
    const finishOption = FINISH_OPTIONS.find(f => f.value === formData.finituraAlluminio);

    // Format finish description with RAL code if needed
    let finishDesc = finishOption ? finishOption.description : '';
    if (formData.ralCustomCode && finishOption?.requiresCode) {
        finishDesc = finishDesc.replace('{CODE}', formData.ralCustomCode);
    }

    // Load template images
    const logoImage = await getImageDataUrl(FIRST_PAGE_LOGO);
    const footerImage = await getImageDataUrl(FOOTER_IMAGE);

    // Load NINFA logo dynamically based on selected model
    let ninfaLogoImage = null;
    let staticPageImage = null;
    let endPageImages = [];

    // Extract number from model name (e.g., "NINFA 106" -> "106")
    const modelNumber = MODEL_RENDER_FOLDER[formData.modelloNinfa] || formData.modelloNinfa.replace(/[^0-9]/g, '');
    if (modelNumber) {
        const ninfaLogoPath = `/images/capitolato/renders/${modelNumber}/logo.png`;
        ninfaLogoImage = await getImageDataUrl(ninfaLogoPath);

        const staticPagePath = `/images/capitolato/renders/${modelNumber}/pagina_statica.jpg`;
        staticPageImage = await getImageDataUrl(staticPagePath);

        // Load only selected images from fine_pagina folder with dimensions
        const selectedImageIndices = formData.selectedEndPageImages || [];

        if (selectedImageIndices.length > 0) {
            const endPageFolder = `/images/capitolato/renders/${modelNumber}/fine_pagina/`;
            const imageLoadPromises = [];

            // Load only selected images in parallel
            for (const imageNum of selectedImageIndices) {
                const imagePath = `${endPageFolder}${imageNum}.jpg`;
                imageLoadPromises.push(
                    getImageDataUrl(imagePath).then(async imageData => {
                        if (imageData && imageData.startsWith('data:image')) {
                            const dimensions = await getImageDimensions(imageData);
                            return {
                                imageData,
                                index: imageNum,
                                width: dimensions.width,
                                height: dimensions.height
                            };
                        }
                        return null;
                    })
                );
            }

            // Wait for all image load attempts to complete
            const results = await Promise.all(imageLoadPromises);

            // Filter out failed loads and add only valid images
            results.forEach(result => {
                if (result) {
                    endPageImages.push({
                        data: result.imageData,
                        index: result.index,
                        width: result.width,
                        height: result.height
                    });
                }
            });

            console.log(`Loaded ${endPageImages.length} selected end page images for model ${modelNumber}:`,
                endPageImages.map(img => `${img.index} (${img.width}x${img.height})`));
        } else {
            console.log(`No images selected for model ${modelNumber}`);
        }
    }

    // Load declaration images (dichiarazioni di conformità) based on model and category
    let declarationImages = [];
    const declFiles = MODEL_DECLARATION_FILES[formData.modelloNinfa];
    if (declFiles) {
        const isTest = formData.categoria && formData.categoria.endsWith('-T');
        const isCalcoli = formData.categoria && formData.categoria.endsWith('-C');
        const declBasePath = '/images/capitolato/dichiarazioni/';
        const declToLoad = [];

        if (isTest && declFiles.test) {
            declToLoad.push(declFiles.test);
        }
        if (isCalcoli && declFiles.calcoli) {
            declToLoad.push(declFiles.calcoli);
        }

        for (const declFile of declToLoad) {
            const declImage = await getImageDataUrl(`${declBasePath}${encodeURIComponent(declFile)}`);
            if (declImage) {
                declarationImages.push(declImage);
                console.log(`Loaded declaration: ${declFile}`);
            } else {
                console.warn(`Failed to load declaration: ${declFile}`);
            }
        }
    }

    const docDefinition = {
        pageSize: 'A4',
        pageMargins: [50, 80, 50, 70], // Margini adattati per logo e footer
        info: {
            title: `Capitolato - ${formData.modelloNinfa} - ${formData.rifCantiere}`,
            author: 'Faraone',
            subject: 'Offerta di Capitolato'
        },

        // Background function to add logo on first page and footer on other pages
        background: function (currentPage, pageSize) {
            const backgrounds = [];

            // First page: Add logo centered at top
            if (currentPage === 1 && logoImage) {
                backgrounds.push({
                    image: logoImage,
                    width: 300,
                    alignment: 'center',
                    margin: [0, 30, 0, 0]
                });
            }

            // All pages: Add footer at bottom (80% width, centered, 20px from bottom)
            // Footer renders on top of static image because it's added after in the backgrounds array
            if (footerImage) {
                const footerWidth = pageSize.width * 0.8;
                const footerX = (pageSize.width - footerWidth) / 2;
                backgrounds.push({
                    image: footerImage,
                    width: footerWidth,
                    absolutePosition: { x: footerX, y: pageSize.height - 60 } // 20px margin bottom + ~40px image height
                });
            }

            return backgrounds;
        },

        content: [
            // PAGE 1: Header and Introduction
            // Spazio per logo Faraone (già nel background)
            { text: '', margin: [0, 0, 0, 120] },

            {
                text: `Data creazione: ${new Date().toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}`,
                style: 'contactInfoBold'
            },
            {
                text: `Nominativi: ${formData.nominativi}`,
                style: 'contactInfoBold'
            },
            {
                text: `Indirizzo: ${formData.indirizzo}`,
                style: 'contactInfoBold'
            },
            {
                text: `Recapito telefonico: ${formData.telefono}`,
                style: 'contactInfoBold'
            },
            {
                text: `Recapito e-mail: ${formData.email}`,
                style: 'contactInfoBold',
                margin: [0, 0, 0, 0]
            },
            { text: '', margin: [0, 0, 0, 20] },

            {
                text: `Rif. Cantiere: ${formData.rifCantiere}`,
                style: 'rifCantiere',
                margin: [0, 0, 0, 15]
            },
            {
                text: `Prodotto: BALAUSTRA ${formData.modelloNinfa}`,
                style: 'productTitle',
                margin: [0, 0, 0, 40]
            },

            // Logo NINFA sulla sinistra
            ...(ninfaLogoImage ? [{
                image: ninfaLogoImage,
                width: 170,
                alignment: 'left',
                margin: [0, -20, 0, 20]
            }] : []),

            {
                text: 'A seguito della richiesta ricevuta, ringraziamo per l\'opportunità e siamo lieti risponderVi.',
                style: 'body',
                margin: [0, 0, 0, 5]
            },
            {
                text: 'E\' utile premettere che la nostra azienda produce e commercializza sistemi di fissaggio per realizzare interventi in vetro, mettendo a disposizione dei progettisti un ufficio in grado di soddisfare esigenze di natura tecnica. In questa fase preliminare, in mancanza di un progetto esecutivo da esaminare, la presente offerta ha l\'obiettivo di determinare un prezzo indicativo per orientarsi sul budget di spesa. L\'eventuale fornitura e messa in opera delle opera sarà realizzata da un ns partner di fiducia. (Faraone Club Expert)',
                style: 'body',
                alignment: 'justify',
                margin: [0, 0, 0, 10]
            },
            {
                text: 'Le voci incluse nella presente quotazione sono:',
                style: 'bodyBold',
                margin: [0, 0, 0, 5]
            },
            {
                ul: [
                    'Sistema di fissaggio in alluminio, per realizzare la balaustra in vetro Ninfa',
                    'Vetri;',
                    'Installazione;',
                    'La fornitura dei profili, dei vetri e la posa'
                ],
                style: 'body',
                margin: [0, 0, 0, 10]
            },
            {
                text: 'Restano escluse dalla presente offerta e dalla nostra fornitura le seguenti voci:',
                style: 'bodyBold',
                margin: [0, 0, 0, 5]
            },
            {
                text: '• Quanto non espressamente citato.',
                style: 'body',
                margin: [0, 0, 0, 0]
            },

            // PAGE BREAK
            { text: '', pageBreak: 'after' },

            // PAGE 2: Normative
            // Logo NINFA
            ...(ninfaLogoImage ? [{
                image: ninfaLogoImage,
                width: 170,
                alignment: 'left',
                margin: [0, 0, 0, 20]
            }] : []),

            {
                text: 'Normative contemplate',
                style: 'headerRed',
                margin: [0, 0, 0, 15]
            },
            {
                text: '1. DM 17/01/2018 - Norme tecniche per le costruzioni',
                style: 'normTitle',
                fillColor: '#FFA500',
                margin: [0, 0, 0, 5]
            },
            {
                text: '2. UNI 7697:2021- Criteri di sicurezza nelle applicazioni vetrarie',
                style: 'normTitle',
                margin: [0, 0, 0, 5]
            },
            {
                text: '3. UNI 11678:2017- Modalità di prova statica e dinamica su parapetti vetrati',
                style: 'normTitle',
                margin: [0, 0, 0, 5]
            },
            {
                text: '4. UNI 10809:1999 - Criteri geometrico prestazionali',
                style: 'normTitle',
                fillColor: '#FFA500',
                margin: [0, 0, 0, 15]
            },

            // DM 17/01/2018 Details
            {
                text: '1. DM 17/01/2018 o "norme tecniche per le costruzioni"',
                style: 'normSubtitle',
                fillColor: '#FFA500',
                margin: [0, 0, 0, 10]
            },
            {
                text: 'E\' la legge che definisce i carichi e le verifiche sulle strutture in Italia. E\' la normativa cogente che va necessariamente applicata sulle strutture e, quindi, anche sui parapetti.',
                style: 'body',
                alignment: 'justify',
                margin: [0, 0, 0, 10]
            },
            {
                text: 'Secondo il DM17/01/2018 i carichi nominali da applicare sulle balaustre sono, a seconda della destinazione d\'uso:',
                style: 'body',
                margin: [0, 0, 0, 5]
            },
            {
                text: '2 kN/m per le categorie A, B, C1, C2;',
                style: 'bodyBold',
                fillColor: '#FFE4B5',
                margin: [0, 0, 0, 2]
            },
            {
                text: '3 kN/m per le categorie C3, C4, C5.',
                style: 'bodyBold',
                fillColor: '#ADD8E6',
                margin: [0, 0, 0, 10]
            },

            // Category Table
            {
                table: {
                    widths: [50, '*', 70],
                    body: [
                        [
                            { text: 'CAT.', style: 'tableHeaderBlack', alignment: 'center', fillColor: '#f39202', margin: [0, 8, 0, 8] },
                            { text: 'AMBIENTI', style: 'tableHeaderBlack', alignment: 'center', fillColor: '#f39202', margin: [0, 8, 0, 8] },
                            { text: 'Hk [kN/m]', style: 'tableHeaderBlack', alignment: 'center', fillColor: '#f39202', margin: [0, 8, 0, 8] }
                        ],
                        [
                            { text: 'A', bold: true, rowSpan: 2, alignment: 'center', margin: [0, 10, 0, 0] },
                            { text: 'Ambienti ad uso residenziale', bold: true },
                            { text: '' }
                        ],
                        [
                            {},
                            { text: 'Scale comuni, balconi, ballatoi' },
                            { text: '2', bold: true, alignment: 'center' }
                        ],
                        [
                            { text: 'B', bold: true, rowSpan: 2, alignment: 'center', margin: [0, 10, 0, 0] },
                            { text: 'Uffici', bold: true },
                            { text: '' }
                        ],
                        [
                            {},
                            { text: 'Scale comuni, balconi, ballatoi' },
                            { text: '2', bold: true, alignment: 'center' }
                        ],
                        [
                            { text: 'C', bold: true, rowSpan: 6, alignment: 'center', margin: [0, 40, 0, 0] },
                            { text: 'Ambienti suscettibili di affollamento', bold: true },
                            { text: '' }
                        ],
                        [
                            {},
                            { text: 'Cat. C2 Aree con posti a sedere fissi, quali chiese, teatri, cinema, sale per conferenze e attesa, aule universitarie e aule magne', fillColor: '#FFF4E6' },
                            { text: '2', bold: true, alignment: 'center', fillColor: '#FFF4E6' }
                        ],
                        [
                            {},
                            { text: 'Cat. C3 Ambienti privi di ostacoli al movimento delle persone, quali musei, sale per esposizioni, aree d\'accesso a uffici, ad alberghi e ospedali, ad atri di stazioni ferroviarie', fillColor: '#E6F3FF' },
                            { text: '3', bold: true, alignment: 'center', fillColor: '#E6F3FF' }
                        ],
                        [
                            {},
                            { text: 'Cat. C4 Aree con possibile svolgimento di attività fisiche, quali sale da ballo, palestre, palcoscenici. 3,00' },
                            { text: '3', bold: true, alignment: 'center' }
                        ],
                        [
                            {},
                            { text: 'Cat. C5 Aree suscettibili di grandi affollamenti, quali edifici per eventi pubblici, sale da concerto, palazzetti per lo sport e relative tribune, gradinate e piattaforme ferroviarie' },
                            { text: '3', bold: true, alignment: 'center' }
                        ],
                        [
                            {},
                            { text: 'Scale comuni, balconi, ballatoi' },
                            { text: '2', bold: true, alignment: 'center' }
                        ]
                    ]
                },
                layout: {
                    hLineWidth: function (i, node) {
                        return 1;
                    },
                    vLineWidth: function (i, node) {
                        return 1;
                    },
                    hLineColor: function (i, node) {
                        return '#000000';
                    },
                    vLineColor: function (i, node) {
                        return '#000000';
                    },
                    paddingLeft: function (i, node) { return 4; },
                    paddingRight: function (i, node) { return 4; },
                    paddingTop: function (i, node) { return 4; },
                    paddingBottom: function (i, node) { return 4; }
                },
                style: 'table',
                margin: [0, 0, 0, 15]
            },

            // PAGE BREAK
            { text: '', pageBreak: 'after' },

            // UNI 7697:2021
            {
                text: '2. UNI 7697:2021 - Criteri di sicurezza nelle applicazioni vetrarie.',
                style: 'normSubtitle',
                margin: [0, 0, 0, 10]
            },
            {
                text: 'Definisce principalmente la scelta della tipologia di vetro da impiegare a seconda della prestazione minima richiesta.',
                style: 'body',
                alignment: 'justify',
                margin: [0, 0, 0, 10]
            },
            {
                text: 'Questa normativa è importante poiché pone l\'attenzione sul criterio PR: La sigla "PR" (post-rottura) implica che i vetri impiegati per realizzare i parapetti non devono collassare immediatamente nel caso in cui tutte le lastre risultino rotte.',
                style: 'body',
                alignment: 'justify',
                margin: [0, 0, 0, 10]
            },
            {
                text: 'Utilizzando un intercalare rigido o una delle due lastre indurite, il Criterio PR è garantito.',
                style: 'body',
                alignment: 'justify',
                margin: [0, 0, 0, 10]
            },

            // Section 3 (attached to section 2, no page break)
            {
                text: '3. UNI 11678:2017. Vetro per edilizia - Elementi di tamponamento in vetro aventi funzione anticaduta - Resistenza al carico statico lineare ed al carico dinamico - Metodi di prova.',
                style: 'normSubtitle',
                margin: [0, 0, 0, 10]
            },
            {
                text: 'Definisce i metodi di prova per determinare il comportamento ai carichi statici linearmente distribuiti e ai carichi dinamici di elementi di tamponamento in vetro avente funzione di anti-caduta. La norma è entrata ufficialmente in vigore in data 11/05/2017 ed è caratterizzata dalle seguenti fasi:',
                style: 'body',
                alignment: 'justify',
                margin: [0, 0, 0, 10]
            },
            {
                ul: [
                    'Controllo della resistenza allo Stato limite Ultimo Esercizio e la relativa flessione massima del vetro in esercizio pari a 100 mm e flessione residua dopo 15 min dalla rimozione del carico pari a 10 mm;',
                    'Controllo della resistenza allo Stato limite Ultimo con amplificazione del carico di esercizio mediante fattore di sicurezza pari a 1,5. Pertanto il carico di 200 kg/m della viene portato a 300 kg/m e il carico di 300 kg/m viene portato a 450 kg/m;',
                    'Controllo della resistenza allo Stato limite di Collasso mediante rottura indotta di una lastra;',
                    'Prova di impatto da corpo duro per la verifica di resistenza del vetro agli urti accidentali di elementi metallici;',
                    'Prova di impatto da corpo semirigido, con determinazione esatta delle altezze di caduta (a seconda della destinazione d\'uso) e dei punti di impatto del pendolo.'
                ],
                style: 'body',
                margin: [0, 0, 0, 15]
            },

            // UNI 10809:1999
            {
                text: '4. UNI 10809:1999 - Ringhiere, balaustre o parapetti prefabbricati.',
                style: 'normSubtitle',
                margin: [0, 0, 0, 10]
            },
            {
                text: [
                    { text: 'Definisce i requisiti geometrico- prestazionali delle balaustre ' },
                    { text: '(altezza minima, scalabilità, inat-traversabilità, impugnabilità del passamano);', bold: true }
                ],
                fontSize: 10,
                lineHeight: 1,
                alignment: 'justify',
                margin: [0, 0, 0, 10]
            },
            {
                text: 'Altezza minima',
                style: 'normSubtitle',
                margin: [0, 0, 0, 10]
            },
            {
                table: {
                    widths: ['*', '*', '*', '*'],
                    body: [
                        [
                            { text: '', border: [true, true, true, true] },
                            { text: 'Uso Pubblico', bold: true, alignment: 'center' },
                            { text: 'Uso privato principale', bold: true, alignment: 'center' },
                            { text: 'Uso privato secondario', bold: true, alignment: 'center' }
                        ],
                        [
                            { text: 'H min. ringhiere', bold: false },
                            { text: '100 cm', alignment: 'center' },
                            { text: '100 cm', alignment: 'center' },
                            { text: '90 cm', alignment: 'center' }
                        ],
                        [
                            { text: 'H min. ringhiere o balaustre', bold: false },
                            { text: '100 cm', alignment: 'center' },
                            { text: '100 cm', alignment: 'center' },
                            { text: '90 cm', alignment: 'center' }
                        ],
                        [
                            { text: 'H min. corrimano', bold: false },
                            { text: '90-100 cm', alignment: 'center' },
                            { text: '90 cm', alignment: 'center' },
                            { text: '90 cm', alignment: 'center' }
                        ]
                    ]
                },
                layout: {
                    hLineWidth: function (i, node) { return 1; },
                    vLineWidth: function (i, node) { return 1; },
                    hLineColor: function (i, node) { return '#000000'; },
                    vLineColor: function (i, node) { return '#000000'; },
                    paddingLeft: function (i, node) { return 8; },
                    paddingRight: function (i, node) { return 8; },
                    paddingTop: function (i, node) { return 6; },
                    paddingBottom: function (i, node) { return 6; }
                },
                margin: [0, 0, 0, 10]
            },
            {
                text: '* Al momento della pubblicazione della presente norma, è in vigore il DM 14 giugno 1989 n°236, capo IV punto 8,1,10',
                fontSize: 9,
                italics: true,
                margin: [0, 0, 0, 10]
            },
            {
                text: [
                    { text: 'Dunque l\'altezza minima è di 100 cm dal piano di calpestio. Fanno eccezione i regolamenti edilizi di alcuni Comuni come, ad esempio, quello di Milano che prevede un\'altezza minima di 110 cm dal piano finito.', bold: true }
                ],
                fontSize: 10,
                lineHeight: 1,
                alignment: 'justify',
                margin: [0, 0, 0, 15]
            },

            // PAGE BREAK
            { text: '', pageBreak: 'after' },

            // Static page: Full-page image with footer on top
            ...(staticPageImage ? [{
                image: staticPageImage,
                width: 595.28,  // A4 width in points (height scales proportionally)
                absolutePosition: { x: 0, y: 0 },
                pageBreak: 'after'
            }] : []),

            // PAGE 4: Descrizione di capitolato
            {
                text: 'Descrizione di capitolato',
                style: 'header',
                margin: [0, 0, 0, 10]
            },
            {
                text: `${formData.modelloNinfa} – Classe ${formData.categoria}`,
                style: 'productSubtitle',
                margin: [0, 0, 0, 15]
            },

            // Logo NINFA
            ...(ninfaLogoImage ? [{
                image: ninfaLogoImage,
                width: 170,
                alignment: 'left',
                margin: [0, 0, 0, 20]
            }] : []),

            // 1) MATERIALI
            {
                text: '1) MATERIALI E COMPONENTI',
                style: 'sectionTitle',
                margin: [0, 0, 0, 5]
            },
            {
                text: 'Parapetto - balaustra o ringhiera, adatto per la posa in opera sopra al pavimento, realizzata con profili estrusi in lega di alluminio posizionati alla base del parapetto. La lega di alluminio è la 6063 T66; il profilo avrà le dimensioni e la geometria interna appositamente ingegnerizzata per poter garantire sia la rigidezza in caso di spinta statica e sia la flessibilità necessaria per assorbire gli urti accidentali in accordo alla UNI 11678:2017.',
                style: 'body',
                alignment: 'justify',
                margin: [0, 0, 0, 15]
            },

            // 2) TIPO VETRO
            {
                text: '2) TIPO VETRO:',
                style: 'sectionTitle',
                margin: [0, 0, 0, 5]
            },
            {
                text: [
                    { text: `${glassOption?.label || ''} `, bold: true, color: '#000' },
                    { text: `${(() => {
                        let desc = glassOption?.description || '';
                        if (formData.modelloNinfa === 'NINFA 56' && formData.categoria && formData.categoria.startsWith('C3')) {
                            desc = desc.replace(
                                'con altezza minima da pavimento pari a 1100 mm.',
                                'con altezza minima da pavimento pari a 1100 mm, considerando che per la configurazione NINFA 56 Classe C3 l\'altezza massima del vetro consentita è pari a 600 mm.'
                            );
                        }
                        return desc;
                    })()}`, color: '#000' }
                ],
                fontSize: 10,
                lineHeight: 1,
                alignment: 'justify',
                margin: [0, 0, 0, 15]
            },

            // 3) ACCESSORI
            {
                text: '3) ACCESSORI:',
                style: 'sectionTitle',
                margin: [0, 0, 0, 5]
            },
            {
                text: 'il fissaggio del vetro sul profilo di alluminio avviene tramite speciali accessori realizzati in PA66 e con registri che permettono la "messa a piombo", l\'allineamento ed il serraggio delle lastre di vetro, operazioni che devono essere fatte solo dal lato interno del parapetto. Sono previsti 4 kit ogni metro di profilo).',
                style: 'body',
                alignment: 'justify',
                margin: [0, 0, 0, 10]
            },
            {
                text: 'Il vetro, sia sul lato esterno sia sul lato interno entra in contatto soltanto con i registri in materiale plastico. Le guarnizioni inserite nelle cave dei profili completano la chiusura superiore tra vetro e profilo in alluminio.',
                style: 'body',
                alignment: 'justify',
                margin: [0, 0, 0, 15]
            },

            // 4) IL FISSAGGIO
            {
                text: '4) IL FISSAGGIO:',
                style: 'sectionTitle',
                margin: [0, 0, 0, 5]
            },
            {
                text: `${fixingOption?.description || ''}`,
                style: 'body',
                alignment: 'justify',
                margin: [0, 0, 0, 15]
            },

            // 5A) FORI DI SCARICO ACQUA
            ...(formData.foriScarico === 'SI' ? [
            {
                text: '5A) FORI DI SCARICO ACQUA:',
                style: 'sectionTitle',
                margin: [0, 0, 0, 5]
            },
            {
                text: drainageOption?.description || '',
                style: 'body',
                alignment: 'justify',
                margin: [0, 0, 0, 15]
            }] : []),

            // 5B) FRESATURA SCARICO ACQUA
            ...(formData.fresaturaScarico === 'SI' ? [
            {
                text: '5B) FRESATURA SCARICO ACQUA:',
                style: 'sectionTitle',
                margin: [0, 0, 0, 5]
            },
            {
                text: millingOption?.description || '',
                style: 'body',
                alignment: 'justify',
                margin: [0, 0, 0, 15]
            }] : []),

            // 5C) PIATTI SCARICO ACQUA
            ...(formData.piattiScarico === 'SI' ? [
            {
                text: '5C) PIATTI SCARICO ACQUA:',
                style: 'sectionTitle',
                margin: [0, 0, 0, 5]
            },
            {
                text: platesOption?.description || '',
                style: 'body',
                alignment: 'justify',
                margin: [0, 0, 0, 15]
            }] : []),

            // 6) FINITURE ALLUMINIO
            {
                text: '6) FINITURE ALLUMINIO:',
                style: 'sectionTitle',
                margin: [0, 0, 0, 5]
            },
            {
                text: `${finishDesc}`,
                style: 'body',
                alignment: 'justify',
                margin: [0, 0, 0, 15]
            },

            // PAGE BREAK
            { text: '', pageBreak: 'after' },

            // PAGE 5: Laboratory Tests and Price
            {
                text: '7) ATTESTATI DI PROVE IN LABORATORIO:',
                style: 'sectionTitle',
                margin: [0, 0, 0, 5]
            },
            {
                text: 'Il parapetto (completo di alluminio, vetro ed accessori) dovrà essere dotato di prove eseguite in laboratorio nel pieno rispetto delle seguenti norme:',
                style: 'body',
                alignment: 'justify',
                margin: [0, 0, 0, 5]
            },
            {
                ol: [
                    'Decreto Ministeriale del 17/1/2018 (norme tecniche per le costruzioni);',
                    'UNI 11678:2017 che identifica la modalità di prova sia statica che dinamica sui parapetti vetrati;',
                    'UNI 7697:2021 definisce la tipologia di vetro da utilizzare ed il requisito post-rottura per parapetti vetrati;',
                    'Disegni tecnici con le dimensioni e lo spessore del vetro, del tipo di interlayer usato per la stratifica del vetro;',
                    'L\'attestato deve riportare tutte le foto del campione durante le fasi delle prove, le quali illustrano anche il tipo di montaggio con il parapetto libero da ogni vincolo laterale. Le singole pagine dovranno essere timbrate dall\'ente certificatore.'
                ],
                style: 'body',
                margin: [0, 0, 0, 10]
            },
            {
                text: 'Il progettista o il direttore dei lavori si riservano di richiedere la prova direttamente in cantiere (con lo stesso sistema e norme usate in laboratorio) per controllarne la rispondenza ed anche la validità del fissaggio del parapetto in opera.',
                style: 'body',
                alignment: 'justify',
                margin: [0, 0, 0, 20]
            },

            // PREZZI
            {
                text: 'Offerta economica',
                style: 'priceTitle',
                margin: [0, 0, 0, 10]
            },
            {
                text: 'Prezzo al metro lineare comprensivo di tutte le voci presenti sulla prima pagina',
                style: 'body',
                margin: [0, 0, 0, 10]
            },
            {
                text: 'Il prezzo indicato non comprende il ricarico d\'impresa',
                style: 'body',
                bold: true,
                margin: [0, 0, 0, 10]
            },
            {
                text: `${formData.prezzo} €/m`,
                style: 'priceValue',
                margin: [0, 0, 0, 5]
            },
            {
                text: `Quantità stimata: ${formData.quantitaStimata} metri lineari`,
                style: 'body',
                bold: true,
                margin: [0, 0, 0, 5]
            },
            {
                text: `Validità offerta: ${formData.validitaOfferta}`,
                style: 'body',
                bold: true,
                margin: [0, 0, 0, formData.note ? 10 : 20]
            },

            // Note section (if present)
            ...(formData.note ? [
                {
                    text: 'Note:',
                    style: 'sectionTitle',
                    margin: [0, 0, 0, 5]
                },
                {
                    text: formData.note,
                    style: 'body',
                    alignment: 'justify',
                    margin: [0, 0, 0, 20]
                }
            ] : []),

            // Declaration pages (dichiarazioni di conformità) - full-page images with footer on top
            ...declarationImages.flatMap(declImg => [
                { text: '', pageBreak: 'after' },
                {
                    image: declImg,
                    width: 595.28,
                    absolutePosition: { x: 0, y: 0 }
                },
                // Footer on top of declaration image (content layer renders above background)
                ...(footerImage ? [{
                    image: footerImage,
                    width: 595.28 * 0.8,
                    absolutePosition: { x: (595.28 - 595.28 * 0.8) / 2, y: 841.89 - 60 }
                }] : [])
            ]),

            // PAGE BREAK before technical section or images
            ...((formData.sezioneTecnicaImages?.length > 0 || endPageImages.length > 0) ? [{ text: '', pageBreak: 'after' }] : []),

            // SEZIONE TECNICA (multiple images, one per page)
            ...(formData.sezioneTecnicaImages && formData.sezioneTecnicaImages.length > 0 ?
                formData.sezioneTecnicaImages.flatMap((img, index) => [
                    {
                        text: `Sezione Tecnica${formData.sezioneTecnicaImages.length > 1 ? ` (${index + 1}/${formData.sezioneTecnicaImages.length})` : ''}`,
                        style: 'priceTitle',
                        margin: [0, 0, 0, 20],
                        alignment: 'center'
                    },
                    {
                        image: img.file,
                        width: 495,
                        alignment: 'center',
                        margin: [0, 0, 0, 20]
                    },
                    // Page break after each image except if it's the last and there are no more sections
                    ...((index < formData.sezioneTecnicaImages.length - 1 || endPageImages.length > 0) ? [{ text: '', pageBreak: 'after' }] : [])
                ])
            : []),

            // Add end page images if available - 3 images per page with intelligent positioning
            ...(endPageImages.length > 0 ? (() => {
                const imageElements = [];
                const pageWidth = 595.28; // A4 width in points
                const pageHeight = 841.89; // A4 height in points
                const pageMargins = [50, 80, 50, 70]; // Current page margins
                const availableWidth = pageWidth - pageMargins[0] - pageMargins[2]; // 495.28
                const availableHeight = pageHeight - pageMargins[1] - pageMargins[3]; // 691.89
                const gap = 10; // Gap between images

                // Classify images by orientation
                const classifiedImages = endPageImages.map(img => {
                    const aspectRatio = img.width / img.height;
                    let orientation;
                    if (aspectRatio > 1.2) orientation = 'horizontal';
                    else if (aspectRatio < 0.8) orientation = 'vertical';
                    else orientation = 'square';
                    return { ...img, aspectRatio, orientation };
                });

                // Process images in groups of 3
                for (let i = 0; i < classifiedImages.length; i += 3) {
                    const pageImages = classifiedImages.slice(i, i + 3);

                    if (pageImages.length === 0) continue;

                    // Determine best layout based on orientations
                    const orientations = pageImages.map(img => img.orientation);
                    const hasVertical = orientations.includes('vertical');
                    const hasHorizontal = orientations.includes('horizontal');
                    const allSameOrientation = orientations.every(o => o === orientations[0]);

                    let pageContent = [];

                    if (pageImages.length === 3) {
                        // 3 images - use intelligent layout
                        if (orientations.filter(o => o === 'vertical').length >= 2) {
                            // 2+ vertical images: stack them in columns
                            const imgWidth = (availableWidth - gap * 2) / 3;
                            pageContent.push({
                                columns: pageImages.map(img => ({
                                    image: img.data,
                                    width: imgWidth,
                                    margin: [0, 0, 0, 0]
                                })),
                                columnGap: gap,
                                margin: [0, 0, 0, 0]
                            });
                        } else if (orientations.filter(o => o === 'horizontal').length === 3) {
                            // 3 horizontal images: stack vertically
                            const imgHeight = (availableHeight - gap * 2) / 3;
                            pageImages.forEach((img, idx) => {
                                const scaledWidth = imgHeight * img.aspectRatio;
                                pageContent.push({
                                    image: img.data,
                                    width: Math.min(scaledWidth, availableWidth),
                                    alignment: 'center',
                                    margin: [0, idx > 0 ? gap : 0, 0, 0]
                                });
                            });
                        } else {
                            // Mixed orientations: 1 on top, 2 on bottom
                            const topImage = pageImages[0];
                            const topHeight = availableHeight * 0.48;
                            const topWidth = Math.min(topHeight * topImage.aspectRatio, availableWidth);

                            pageContent.push({
                                image: topImage.data,
                                width: topWidth,
                                alignment: 'center',
                                margin: [0, 0, 0, gap]
                            });

                            // Bottom row with 2 images
                            const bottomImages = pageImages.slice(1);
                            const bottomHeight = availableHeight * 0.48;
                            const bottomImgWidth = (availableWidth - gap) / 2;

                            if (bottomImages.length === 2) {
                                pageContent.push({
                                    columns: bottomImages.map(img => {
                                        const scaledHeight = bottomImgWidth / img.aspectRatio;
                                        const finalHeight = Math.min(scaledHeight, bottomHeight);
                                        const finalWidth = finalHeight * img.aspectRatio;
                                        return {
                                            image: img.data,
                                            width: Math.min(finalWidth, bottomImgWidth),
                                            alignment: 'center',
                                            margin: [0, 0, 0, 0]
                                        };
                                    }),
                                    columnGap: gap,
                                    margin: [0, 0, 0, 0]
                                });
                            } else if (bottomImages.length === 1) {
                                const img = bottomImages[0];
                                const scaledWidth = bottomHeight * img.aspectRatio;
                                pageContent.push({
                                    image: img.data,
                                    width: Math.min(scaledWidth, availableWidth),
                                    alignment: 'center',
                                    margin: [0, 0, 0, 0]
                                });
                            }
                        }
                    } else if (pageImages.length === 2) {
                        // 2 images - side by side or stacked
                        if (hasVertical) {
                            // Side by side
                            const imgWidth = (availableWidth - gap) / 2;
                            pageContent.push({
                                columns: pageImages.map(img => ({
                                    image: img.data,
                                    width: imgWidth,
                                    margin: [0, 0, 0, 0]
                                })),
                                columnGap: gap,
                                margin: [0, 0, 0, 0]
                            });
                        } else {
                            // Stacked vertically
                            const imgHeight = (availableHeight - gap) / 2;
                            pageImages.forEach((img, idx) => {
                                const scaledWidth = imgHeight * img.aspectRatio;
                                pageContent.push({
                                    image: img.data,
                                    width: Math.min(scaledWidth, availableWidth),
                                    alignment: 'center',
                                    margin: [0, idx > 0 ? gap : 0, 0, 0]
                                });
                            });
                        }
                    } else if (pageImages.length === 1) {
                        // 1 image - centered
                        const img = pageImages[0];
                        let imgWidth = availableWidth;
                        let imgHeight = imgWidth / img.aspectRatio;

                        if (imgHeight > availableHeight) {
                            imgHeight = availableHeight;
                            imgWidth = imgHeight * img.aspectRatio;
                        }

                        pageContent.push({
                            image: img.data,
                            width: imgWidth,
                            alignment: 'center',
                            margin: [0, 0, 0, 0]
                        });
                    }

                    // Add page content and page break
                    imageElements.push(...pageContent);
                    if (i + 3 < classifiedImages.length) {
                        imageElements.push({ text: '', pageBreak: 'after' });
                    }
                }

                return imageElements;
            })() : [])
        ],

        styles: {
            header: {
                fontSize: 16,
                bold: true
            },
            headerRed: {
                fontSize: 16,
                bold: true,
                color: '#CC1F1F' // Rosso Faraone
            },
            rifCantiere: {
                fontSize: 13,
                bold: true,
                margin: [0, 0, 0, 2]
            },
            productTitle: {
                fontSize: 16,
                bold: true,
                color: '#CC1F1F' // Rosso Faraone
            },
            productSubtitle: {
                fontSize: 14,
                bold: true,
                color: '#2E7D32' // Verde per sottotitolo
            },
            contactInfoBold: {
                fontSize: 11,
                bold: true,
                margin: [0, 0, 0, 3]
            },
            contactInfo: {
                fontSize: 10,
                margin: [0, 0, 0, 2]
            },
            body: {
                fontSize: 10,
                lineHeight: 1,
                alignment: 'justify'
            },
            bodyBlue: {
                fontSize: 10,
                lineHeight: 1,
                color: '#0066CC' // Blu come nel PDF
            },
            bodyRed: {
                fontSize: 10,
                lineHeight: 1,
                color: '#CC0000' // Rosso scuro
            },
            bodyPurple: {
                fontSize: 10,
                lineHeight: 1,
                color: '#6B0099' // Viola
            },
            bodyBold: {
                fontSize: 10,
                lineHeight: 1,
                bold: true,
                alignment: 'justify'
            },
            normTitle: {
                fontSize: 11,
                bold: true,
                margin: [0, 3, 0, 3]
            },
            normSubtitle: {
                fontSize: 11,
                bold: true,
                italics: true
            },
            sectionTitle: {
                fontSize: 11,
                bold: true,
                margin: [0, 5, 0, 5]
            },
            table: {
                fontSize: 9,
                margin: [0, 2, 0, 2]
            },
            tableHeader: {
                fontSize: 9,
                bold: true,
                color: '#FFFFFF'
            },
            tableHeaderBlack: {
                fontSize: 9,
                bold: true,
                color: '#000000'
            },
            priceTitle: {
                fontSize: 16,
                bold: true,
                color: '#CC1F1F' // Rosso Faraone
            },
            priceValue: {
                fontSize: 14,
                bold: true,
                margin: [0, 5, 0, 5]
            }
        },

        defaultStyle: {
            fontSize: 10
        }
    };

    return docDefinition;
};

// Main function to generate and download PDF (also returns blob for backend storage)
export const generateCapitolatoPDF = async (formData) => {
    try {
        const docDefinition = await createDocDefinition(formData);
        const filename = `Capitolato_${formData.modelloNinfa.replace(/\s+/g, '_')}_${formData.rifCantiere.replace(/\s+/g, '_')}_${new Date().getTime()}.pdf`;

        return new Promise((resolve, reject) => {
            const pdfDoc = pdfMake.createPdf(docDefinition);
            pdfDoc.getBlob((blob) => {
                // Trigger browser download
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);

                resolve({ success: true, filename, blob });
            });
        });
    } catch (error) {
        console.error('Error generating PDF:', error);
        throw error;
    }
};

// Function to open PDF in new window for preview
export const previewCapitolatoPDF = async (formData) => {
    try {
        const docDefinition = await createDocDefinition(formData);
        pdfMake.createPdf(docDefinition).open();
        return { success: true };
    } catch (error) {
        console.error('Error generating PDF preview:', error);
        throw error;
    }
};

// Build a merged docDefinition for multiple configurations sharing the same contact info
const createGroupedDocDefinition = async (contactInfo, configurations) => {
    const mergedConfigs = configurations.map(config => ({
        ...config,
        ...contactInfo
    }));

    // Generate individual docDefinitions
    const docDefs = [];
    for (const merged of mergedConfigs) {
        docDefs.push(await createDocDefinition(merged));
    }

    // Use first docDef as base (styles, background, margins, etc.)
    const base = docDefs[0];

    // Load Faraone logo for embedding in content of configs 2+
    const logoImage = await getImageDataUrl(FIRST_PAGE_LOGO);

    // Concatenate content: first config as-is, subsequent configs get a pageBreak
    // and the Faraone logo embedded as content (since background only shows it on absolute page 1)
    let allContent = [...base.content];

    for (let i = 1; i < docDefs.length; i++) {
        allContent.push({ text: '', pageBreak: 'after' });

        // Embed Faraone logo as content for this config's first page
        if (logoImage) {
            allContent.push({
                image: logoImage,
                width: 300,
                alignment: 'center',
                margin: [0, 30, 0, 0]
            });
        }

        // Replace the spacer that assumes background logo (first element is { text: '', margin: [0, 0, 0, 120] })
        // Skip it since we embedded the logo as content — but we still need some spacing
        const configContent = docDefs[i].content;
        const startIndex = (configContent[0]?.text === '' && configContent[0]?.margin?.[3] === 120) ? 1 : 0;

        for (let j = startIndex; j < configContent.length; j++) {
            allContent.push(configContent[j]);
        }
    }

    base.content = allContent;
    base.info.title = `Capitolato Gruppo - ${contactInfo.rifCantiere}`;

    return base;
};

// Generate and download a grouped PDF (multiple configurations, one document)
export const generateGroupedCapitolatoPDF = async (contactInfo, configurations) => {
    try {
        const docDefinition = await createGroupedDocDefinition(contactInfo, configurations);
        const filename = `Capitolato_Gruppo_${contactInfo.rifCantiere.replace(/\s+/g, '_')}_${new Date().getTime()}.pdf`;

        return new Promise((resolve, reject) => {
            const pdfDoc = pdfMake.createPdf(docDefinition);
            pdfDoc.getBlob((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);

                resolve({ success: true, filename, blob });
            });
        });
    } catch (error) {
        console.error('Error generating grouped PDF:', error);
        throw error;
    }
};

// Preview a grouped PDF in a new window
export const previewGroupedCapitolatoPDF = async (contactInfo, configurations) => {
    try {
        const docDefinition = await createGroupedDocDefinition(contactInfo, configurations);
        pdfMake.createPdf(docDefinition).open();
        return { success: true };
    } catch (error) {
        console.error('Error previewing grouped PDF:', error);
        throw error;
    }
};
